import React from 'react';
import SettingsSection from '../../settings-and-privacy/components/SettingsSection';
import SettingItem from '../../settings-and-privacy/components/SettingItem';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const DeliverySettingsTab = ({ settings, onSettingChange, onRootSettingChange, loading }) => {
  const batchingIntervalOptions = [
    { value: 'realtime', label: 'Real-time (No batching)' },
    { value: 'hourly', label: 'Hourly' },
    { value: '4hours', label: 'Every 4 hours' },
    { value: 'daily', label: 'Daily digest' }
  ];

  const timezoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: Intl.DateTimeFormat()?.resolvedOptions()?.timeZone, label: 'System Default' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Delivery Methods */}
      <SettingsSection
        title="Delivery Methods"
        description="Choose how you want to receive notifications"
        icon="Send"
        collapsible={false}
      >
        <div className="space-y-3">
          <Checkbox
            checked={settings?.deliveryMethods?.email}
            onChange={(e) => onSettingChange('deliveryMethods', 'email', e?.target?.checked)}
            label="Email Notifications"
            description="Receive alerts via email for all priority levels"
          />
          <Checkbox
            checked={settings?.deliveryMethods?.inApp}
            onChange={(e) => onSettingChange('deliveryMethods', 'inApp', e?.target?.checked)}
            label="In-App Notifications"
            description="Show notifications within the application interface"
          />
          <Checkbox
            checked={settings?.deliveryMethods?.sms}
            onChange={(e) => onSettingChange('deliveryMethods', 'sms', e?.target?.checked)}
            label="SMS Notifications (High Priority Only)"
            description="Receive text messages for critical security alerts"
          />
        </div>
      </SettingsSection>
      {/* Quiet Hours */}
      <SettingsSection
        title="Quiet Hours"
        description="Pause non-critical notifications during specific hours"
        icon="Moon"
        collapsible={true}
        defaultOpen={true}
      >
        <SettingItem
          label="Enable Quiet Hours"
          description="Suppress low-priority notifications during quiet hours"
          type="toggle"
          value={settings?.quietHours?.enabled}
          onChange={(value) => onSettingChange('quietHours', 'enabled', value)}
          min={undefined}
          max={undefined}
          onButtonClick={undefined}
          icon={undefined}
        />
        
        {settings?.quietHours?.enabled && (
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                type="time"
                label="Start Time"
                value={settings?.quietHours?.start}
                onChange={(e) => onSettingChange('quietHours', 'start', e?.target?.value)}
              />
              <Input
                type="time"
                label="End Time"
                value={settings?.quietHours?.end}
                onChange={(e) => onSettingChange('quietHours', 'end', e?.target?.value)}
              />
            </div>
            <Select
              label="Timezone"
              value={settings?.quietHours?.timezone}
              onChange={(value) => onSettingChange('quietHours', 'timezone', value)}
              options={timezoneOptions}
            />
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" strokeWidth={2} />
              <p className="text-sm text-foreground">
                <strong>Note:</strong> Critical security alerts will bypass quiet hours and be delivered immediately.
              </p>
            </div>
          </div>
        )}
      </SettingsSection>
      {/* Notification Batching */}
      <SettingsSection
        title="Notification Batching"
        description="Group multiple low-priority alerts into digest format"
        icon="Layers"
        collapsible={true}
        defaultOpen={true}
      >
        <SettingItem
          label="Enable Batching"
          description="Consolidate multiple non-urgent alerts into periodic summaries"
          type="toggle"
          value={settings?.batching?.enabled}
          onChange={(value) => onSettingChange('batching', 'enabled', value)}
          min={undefined}
          max={undefined}
          onButtonClick={undefined}
          icon={undefined}
        />
        
        {settings?.batching?.enabled && (
          <div className="space-y-4 mt-4">
            <Select
              label="Batching Interval"
              description="How often to send batched notification summaries"
              value={settings?.batching?.interval}
              onChange={(value) => onSettingChange('batching', 'interval', value)}
              options={batchingIntervalOptions}
            />
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Minimum Alerts for Batch
              </label>
              <p className="text-sm text-muted-foreground">
                Minimum number of alerts before creating a batch
              </p>
              <input
                type="range"
                min={1}
                max={10}
                step={1}
                value={settings?.batching?.minAlerts}
                onChange={(e) => onSettingChange('batching', 'minAlerts', parseInt(e?.target?.value))}
                className="w-full"
              />
              <div className="text-sm text-muted-foreground text-right">
                {settings?.batching?.minAlerts}
              </div>
            </div>
          </div>
        )}
      </SettingsSection>
      {/* Emergency Override */}
      <SettingsSection
        title="Emergency Override"
        description="Critical alert handling"
        icon="AlertCircle"
        collapsible={false}
      >
        <SettingItem
          label="Enable Emergency Override"
          description="Critical security alerts bypass all quiet hours and batching restrictions"
          type="toggle"
          value={settings?.emergencyOverride}
          onChange={(value) => onRootSettingChange('emergencyOverride', value)}
          min={undefined}
          max={undefined}
          onButtonClick={undefined}
          icon={undefined}
        />
        
        {settings?.emergencyOverride && (
          <div className="mt-4 bg-error/10 border border-error/20 rounded-lg p-4 flex items-start gap-3">
            <Icon name="ShieldAlert" size={20} className="text-error flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div>
              <p className="text-sm font-semibold text-foreground mb-1">Emergency Override Active</p>
              <p className="text-sm text-muted-foreground">
                Security violations and critical alerts will be delivered immediately via all enabled channels, regardless of quiet hours or batching settings.
              </p>
            </div>
          </div>
        )}
      </SettingsSection>
      {/* Delivery Preview */}
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-start gap-3 mb-4">
          <Icon name="Eye" size={20} className="text-primary flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-foreground mb-2">Notification Preview</h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                <strong className="text-foreground">Active Channels:</strong>{' '}
                {Object.entries(settings?.deliveryMethods || {})
                  ?.filter(([_, enabled]) => enabled)
                  ?.map(([method]) => method?.charAt(0)?.toUpperCase() + method?.slice(1))
                  ?.join(', ') || 'None'}
              </p>
              <p>
                <strong className="text-foreground">Quiet Hours:</strong>{' '}
                {settings?.quietHours?.enabled 
                  ? `${settings?.quietHours?.start} - ${settings?.quietHours?.end}` 
                  : 'Disabled'}
              </p>
              <p>
                <strong className="text-foreground">Batching:</strong>{' '}
                {settings?.batching?.enabled 
                  ? `${settings?.batching?.interval} (min ${settings?.batching?.minAlerts} alerts)` 
                  : 'Disabled'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliverySettingsTab;